import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  DataSource,
  MoonshotSignalsArgs,
  ResearchReportCreateResult,
} from "../api/dataSource";
import type {
  MoonshotAIRankResult,
  MoonshotProviderStatus,
  MoonshotSignalsSnapshot,
  MoonshotStockSignal,
  ResearchSuggestedSignal,
} from "../types";

type Props = {
  listMoonshotSignals: DataSource["listMoonshotSignals"];
  rankMoonshotTopTen: DataSource["rankMoonshotTopTen"];
  createResearchReport: DataSource["createResearchReport"];
};

const AUTO_REFRESH_MS = 5 * 60 * 1000;
const DEFAULT_UNIVERSE = "SP500";
const DEFAULT_LIMIT = "50";
const DEFAULT_MAX_PRICE = "20";

type TaskFilter =
  | "top_ranked"
  | "needs_deep_dive"
  | "has_setup"
  | "high_volume"
  | "provider_gaps";
type SortMode = "ranked" | "ai_ranked";

const emptySnapshot = (): MoonshotSignalsSnapshot => ({
  generatedAt: "",
  universe: "",
  sample: false,
  providerStatuses: [],
  rows: [],
});

const formatPrice = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatPercent = (value: number): string =>
  `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;

const formatDate = (value: string | null): string => {
  if (!value) return "Not wired";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const providerStatusText = (status: MoonshotProviderStatus): string =>
  `${status.source.replaceAll("_", " ")}: ${status.status}`;

const boundedLimit = (value: string): number => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return Number(DEFAULT_LIMIT);
  return Math.max(1, Math.min(200, parsed));
};

const positiveNumberOrUndefined = (value: string): number | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

type DeepDiveState = {
  status: "running" | "done" | "failed";
  message: string;
  result: ResearchReportCreateResult | null;
};

const suggestedSignalTitle = (signal: ResearchSuggestedSignal): string => {
  if (signal.stance === "flat") return "No trade setup";
  return `${signal.stance.toUpperCase()} setup`;
};

const setupLabel = (deepDive: DeepDiveState | undefined): string => {
  if (!deepDive) return "Needs deep dive";
  if (deepDive.status === "running") return "Generating";
  if (deepDive.status === "failed") return "Deep dive failed";
  const signal = deepDive.result?.suggestedSignal;
  if (!signal) return "Report archived";
  return signal.stance === "flat" ? "No setup" : "Display-only setup";
};

const SuggestedSetupPanel = ({
  signal,
}: {
  signal: ResearchSuggestedSignal;
}) => (
  <section className="tp-moonshot-suggested-setup">
    <div className="tp-tile-head" style={{ padding: 0 }}>
      <h2>Suggested setup</h2>
      <span className="tp-chip info">Display only</span>
    </div>
    <div className="tp-moonshot-setup-summary">
      <strong>{suggestedSignalTitle(signal)}</strong>
      <span>{signal.validationStatus.replaceAll("_", " ")}</span>
    </div>
    {signal.stance === "flat" ? (
      <p className="tp-moonshot-note">{signal.thesisSummary}</p>
    ) : (
      <div className="tp-moonshot-metrics">
        <div>
          <span>Entry</span>
          <strong className="num">{signal.entryPrice ?? "—"}</strong>
          <small>{signal.strategyId ?? signal.source}</small>
        </div>
        <div>
          <span>Stop</span>
          <strong className="num">{signal.stopPrice ?? "—"}</strong>
          <small>{signal.invalidationText || "Deterministic invalidation"}</small>
        </div>
        <div>
          <span>Targets</span>
          <strong className="num">
            {signal.targetPrices.length > 0 ? signal.targetPrices.join(" / ") : "—"}
          </strong>
          <small>R/R {signal.riskRewardRatio ?? "—"}</small>
        </div>
      </div>
    )}
    {signal.thesisSummary && signal.stance !== "flat" ? (
      <p className="tp-moonshot-note">{signal.thesisSummary}</p>
    ) : null}
    <small className="tp-muted">{signal.llmAdjustmentNote}</small>
  </section>
);

export const MoonshotSignalsScreen = ({
  listMoonshotSignals,
  rankMoonshotTopTen,
  createResearchReport,
}: Props) => {
  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("all");
  const [taskFilter, setTaskFilter] = useState<TaskFilter>("top_ranked");
  const [sortMode, setSortMode] = useState<SortMode>("ranked");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [universe, setUniverse] = useState(DEFAULT_UNIVERSE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [maxPrice, setMaxPrice] = useState(DEFAULT_MAX_PRICE);
  const [asOf, setAsOf] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [snapshot, setSnapshot] = useState<MoonshotSignalsSnapshot>(
    emptySnapshot,
  );
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "failed">(
    "loading",
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(null);
  const [deepDives, setDeepDives] = useState<Record<string, DeepDiveState>>({});
  const [aiRank, setAiRank] = useState<MoonshotAIRankResult | null>(null);
  const [aiRankState, setAiRankState] = useState<
    "idle" | "running" | "done" | "failed"
  >("idle");
  const [aiRankMessage, setAiRankMessage] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const requestArgs = useCallback(
    (options: { forceRefresh?: boolean } = {}): MoonshotSignalsArgs => {
      const args: MoonshotSignalsArgs = {
        asOf: asOf || undefined,
        universe: universe.trim().toUpperCase() || DEFAULT_UNIVERSE,
        limit: boundedLimit(limit),
        maxPrice: positiveNumberOrUndefined(maxPrice),
      };
      if (options.forceRefresh) {
        args.forceRefresh = true;
      }
      return args;
    },
    [asOf, limit, maxPrice, universe],
  );

  const loadSignals = useCallback(
    (options: { forceRefresh?: boolean } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (!hasLoadedRef.current) {
        setLoadState("loading");
      }
      setIsRefreshing(true);
      setError(null);
      listMoonshotSignals(requestArgs(options))
        .then((next) => {
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          setSnapshot(next);
          setSelectedTicker((current) =>
            current && next.rows.some((row) => row.ticker === current)
              ? current
              : next.rows[0]?.ticker ?? null,
          );
          setAiRank(null);
          setSortMode("ranked");
          setAiRankState("idle");
          setAiRankMessage(null);
          hasLoadedRef.current = true;
          setLoadState("loaded");
        })
        .catch((e) => {
          if (!mountedRef.current || requestId !== requestIdRef.current) return;
          setError(e instanceof Error ? e.message : "Moonshot data unavailable");
          setLoadState("failed");
        })
        .finally(() => {
          if (mountedRef.current && requestId === requestIdRef.current) {
            setIsRefreshing(false);
          }
        });
    },
    [listMoonshotSignals, requestArgs],
  );

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = window.setInterval(() => {
      loadSignals();
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(id);
  }, [autoRefresh, loadSignals]);

  const sectors = useMemo(
    () => Array.from(new Set(snapshot.rows.map((row) => row.sector))).sort(),
    [snapshot.rows],
  );

  const aiRankByTicker = useMemo(() => {
    const map = new Map<string, MoonshotAIRankResult["rows"][number]>();
    aiRank?.rows.forEach((row) => map.set(row.ticker, row));
    return map;
  }, [aiRank]);

  const rows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = snapshot.rows.filter((row) => {
      const deepDive = deepDives[row.ticker];
      const matchesSector = sector === "all" || row.sector === sector;
      const matchesQuery =
        normalized === "" ||
        row.ticker.toLowerCase().includes(normalized) ||
        row.companyName.toLowerCase().includes(normalized) ||
        row.sector.toLowerCase().includes(normalized);
      const matchesTask =
        taskFilter === "top_ranked" ||
        (taskFilter === "needs_deep_dive" && !deepDive) ||
        (taskFilter === "has_setup" && Boolean(deepDive?.result?.suggestedSignal)) ||
        (taskFilter === "high_volume" && row.unusualVolumeRatio >= 2.5) ||
        (taskFilter === "provider_gaps" && row.dataQuality !== "complete");
      return matchesSector && matchesQuery && matchesTask;
    });

    return [...filtered].sort((a, b) => {
      if (sortMode === "ai_ranked" && aiRankByTicker.size > 0) {
        const aiA = aiRankByTicker.get(a.ticker);
        const aiB = aiRankByTicker.get(b.ticker);
        if (aiA && aiB) return aiA.aiRank - aiB.aiRank;
        if (aiA) return -1;
        if (aiB) return 1;
      }
      return a.rank - b.rank;
    });
  }, [aiRankByTicker, deepDives, query, sector, snapshot.rows, sortMode, taskFilter]);

  const selectedStock = useMemo(() => {
    if (rows.length === 0) return null;
    return rows.find((row) => row.ticker === selectedTicker) ?? rows[0];
  }, [rows, selectedTicker]);

  useEffect(() => {
    if (!selectedStock && rows[0]) {
      setSelectedTicker(rows[0].ticker);
    }
  }, [rows, selectedStock]);

  const runDeepDive = (ticker: string) => {
    const symbol = ticker.trim().toUpperCase();
    if (!symbol) return;
    setDeepDives((current) => ({
      ...current,
      [symbol]: {
        status: "running",
        message: "Generating deep dive.",
        result: null,
      },
    }));
    createResearchReport({
      symbol,
      template: "deep_dive_v1",
      forceRegenerate: false,
    })
      .then((result) => {
        if (!mountedRef.current) return;
        setDeepDives((current) => ({
          ...current,
          [symbol]: {
            status: "done",
            message: result.cacheHit
              ? "Deep dive loaded from archive; setup refreshed."
              : "Deep dive generated and archived.",
            result,
          },
        }));
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        setDeepDives((current) => ({
          ...current,
          [symbol]: {
            status: "failed",
            message: e instanceof Error ? e.message : "Deep dive failed",
            result: null,
          },
        }));
      });
  };

  const runAiRank = () => {
    setAiRankState("running");
    setAiRankMessage(null);
    rankMoonshotTopTen(requestArgs())
      .then((result) => {
        if (!mountedRef.current) return;
        setAiRank(result);
        setAiRankState(result.status === "degraded" ? "failed" : "done");
        setAiRankMessage(result.detail);
        if (result.rows.length > 0) {
          setSortMode("ai_ranked");
        }
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        setAiRankState("failed");
        setAiRankMessage(e instanceof Error ? e.message : "AI review failed");
      });
  };

  const generatedAtText = snapshot.generatedAt
    ? new Date(snapshot.generatedAt).toLocaleString()
    : "loading";
  const meta = snapshot.sample
    ? "sample research queue - not live data"
    : `${snapshot.universe || "production"} research queue - ${generatedAtText}`;

  const highVolumeCount = snapshot.rows.filter(
    (row) => row.unusualVolumeRatio >= 2.5,
  ).length;
  const providerGapCount = snapshot.rows.filter(
    (row) => row.dataQuality !== "complete",
  ).length;
  const setupCount = Object.values(deepDives).filter(
    (item) => item.result?.suggestedSignal,
  ).length;

  return (
    <div data-screen-label="Moonshot Signals">
      <div className="tp-page-head">
        <h1>Moonshot Research Queue</h1>
        <div className="meta">{meta}</div>
      </div>

      <div className="tp-banner warn tp-moonshot-disclaimer" role="status">
        <span className="icon">!</span>
        <span>
          Not investment advice. These are research signals for high-risk,
          often pre-revenue or volatile stocks. Do your own due diligence;
          data can be delayed or incomplete and is not an enter-now instruction.
        </span>
      </div>

      {snapshot.sample ? (
        <div className="tp-banner info" role="status">
          <span className="icon">i</span>
          <span>
            Fixture mode is showing sample data only. Production mode reads from
            the backend Moonshots aggregation endpoint.
          </span>
        </div>
      ) : null}

      {error ? (
        <div className="tp-banner bad" role="alert">
          <span className="icon">!</span>
          <span>{error}</span>
        </div>
      ) : null}

      <section className="tp-tile tp-moonshot-toolbar">
        <div className="tp-moonshot-toolbar-main">
          <label>
            <span>Search</span>
            <input
              className="tp-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Ticker, name, or sector"
              aria-label="Search moonshot queue"
            />
          </label>
          <label>
            <span>Sector</span>
            <select
              className="tp-input"
              value={sector}
              onChange={(event) => setSector(event.target.value)}
              aria-label="Filter moonshot sector"
            >
              <option value="all">All sectors</option>
              {sectors.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>View</span>
            <select
              className="tp-input"
              value={taskFilter}
              onChange={(event) => setTaskFilter(event.target.value as TaskFilter)}
              aria-label="Filter moonshot task view"
            >
              <option value="top_ranked">Top ranked</option>
              <option value="needs_deep_dive">Needs deep dive</option>
              <option value="has_setup">Has setup</option>
              <option value="high_volume">High volume</option>
              <option value="provider_gaps">Provider gaps</option>
            </select>
          </label>
          <div className="tp-moonshot-count">
            <strong>{rows.length}</strong>
            <span>
              of {snapshot.rows.length} {snapshot.sample ? "sample " : ""}
              candidates
            </span>
          </div>
          <div className="tp-moonshot-actions">
            <div className="tp-segmented" role="group" aria-label="Moonshot sort">
              <button
                type="button"
                className={sortMode === "ranked" ? "active" : ""}
                onClick={() => setSortMode("ranked")}
              >
                Ranked
              </button>
              <button
                type="button"
                className={sortMode === "ai_ranked" ? "active" : ""}
                onClick={() => setSortMode("ai_ranked")}
                disabled={!aiRank || aiRank.rows.length === 0}
              >
                AI ranked
              </button>
            </div>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              onClick={runAiRank}
              disabled={
                aiRankState === "running" ||
                snapshot.rows.length === 0 ||
                loadState === "loading"
              }
            >
              {aiRankState === "running" ? "Reviewing" : "AI review top 10"}
            </button>
            <label className="tp-moonshot-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(event) => setAutoRefresh(event.target.checked)}
                aria-label="Auto refresh moonshot signals"
              />
              <span>Auto refresh</span>
            </label>
            <button
              type="button"
              className="tp-btn tp-btn-secondary tp-btn-sm"
              onClick={() => {
                loadSignals({ forceRefresh: true });
              }}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Refreshing" : "Refresh"}
            </button>
          </div>
        </div>
        {aiRankMessage ? (
          <div
            className={`tp-inline-status ${
              aiRankState === "failed" ? "bad" : ""
            }`}
          >
            {aiRankMessage}
          </div>
        ) : null}
        <button
          type="button"
          className="tp-moonshot-advanced-toggle"
          onClick={() => setAdvancedOpen((value) => !value)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? "Hide advanced filters" : "Advanced filters"}
        </button>
        {advancedOpen ? (
          <div className="tp-moonshot-advanced">
            <label>
              <span>Universe</span>
              <input
                className="tp-input"
                value={universe}
                onChange={(event) => setUniverse(event.target.value)}
                aria-label="Moonshot universe"
              />
            </label>
            <label>
              <span>Limit</span>
              <input
                className="tp-input"
                type="number"
                min="1"
                max="200"
                value={limit}
                onChange={(event) => setLimit(event.target.value)}
                aria-label="Moonshot row limit"
              />
            </label>
            <label>
              <span>Max price</span>
              <input
                className="tp-input"
                type="number"
                min="0.01"
                step="0.01"
                value={maxPrice}
                onChange={(event) => setMaxPrice(event.target.value)}
                aria-label="Moonshot max price"
              />
            </label>
            <label>
              <span>As of</span>
              <input
                className="tp-input"
                type="date"
                value={asOf}
                onChange={(event) => setAsOf(event.target.value)}
                aria-label="Moonshot as-of date"
              />
            </label>
          </div>
        ) : null}
      </section>

      <div className="tp-moonshot-provider-strip">
        {snapshot.providerStatuses.map((status) => (
          <span
            className={`tp-status tp-status-compact ${
              status.status === "ready"
                ? "ok"
                : status.status === "degraded"
                  ? "warn"
                  : "idle"
            }`}
            title={status.detail}
            key={status.source}
          >
            <span className="dot" />
            {providerStatusText(status)}
          </span>
        ))}
      </div>

      <div className="tp-moonshot-summary">
        <div>
          <span>High volume</span>
          <strong className="num">{highVolumeCount}</strong>
        </div>
        <div>
          <span>Provider gaps</span>
          <strong className="num">{providerGapCount}</strong>
        </div>
        <div>
          <span>Suggested setups</span>
          <strong className="num">{setupCount}</strong>
        </div>
        <div>
          <span>AI reviewed</span>
          <strong className="num">{aiRank?.rows.length ?? 0}</strong>
        </div>
      </div>

      <div className="tp-moonshot-workspace">
        <section className="tp-tile tp-moonshot-table-card">
          {loadState === "loading" ? (
            <div className="tp-empty-state">Loading moonshot research queue.</div>
          ) : rows.length === 0 ? (
            <div className="tp-empty-state">No stocks match this filter.</div>
          ) : (
            <div className="tp-moonshot-table-wrap">
              <table className="tp-moonshot-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Symbol</th>
                    <th>Why ranked</th>
                    <th>Price</th>
                    <th>Volume</th>
                    <th>Catalyst / news</th>
                    <th>Risk</th>
                    <th>Setup</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((stock) => {
                    const deepDive = deepDives[stock.ticker];
                    const ai = aiRankByTicker.get(stock.ticker);
                    const selected = selectedStock?.ticker === stock.ticker;
                    return (
                      <tr
                        key={stock.ticker}
                        className={selected ? "selected" : ""}
                        onClick={() => setSelectedTicker(stock.ticker)}
                      >
                        <td>
                          <div className="tp-moonshot-rank-cell">
                            <strong>
                              #{sortMode === "ai_ranked" && ai ? ai.aiRank : stock.rank}
                            </strong>
                            <span>{stock.researchPriorityScore}</span>
                            {ai ? <small>AI {ai.aiScore}</small> : null}
                          </div>
                        </td>
                        <td>
                          <div className="tp-moonshot-symbol-cell">
                            <strong>{stock.ticker}</strong>
                            <span>{stock.companyName}</span>
                            <small>{stock.sector}</small>
                          </div>
                        </td>
                        <td>
                          <div className="tp-moonshot-reasons">
                            {(ai ? [ai.aiReason] : stock.rankReasons).slice(0, 3).map(
                              (reason) => (
                                <span key={`${stock.ticker}-${reason}`}>{reason}</span>
                              ),
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tp-moonshot-price-cell">
                            <span className="num">{formatPrice(stock.currentPrice)}</span>
                            <span
                              className={`num ${
                                stock.percentChange > 0
                                  ? "pos"
                                  : stock.percentChange < 0
                                    ? "neg"
                                    : "flat"
                              }`}
                            >
                              {formatPercent(stock.percentChange)}
                            </span>
                          </div>
                        </td>
                        <td className="num">
                          {stock.unusualVolumeRatio.toFixed(1)}x
                        </td>
                        <td>
                          <span>{formatDate(stock.nextEarningsDate)}</span>
                          <small>{stock.recentNews.length} news</small>
                        </td>
                        <td>
                          <span
                            className={`tp-status tp-status-compact ${
                              stock.riskTier === "Speculative" ? "warn" : "info"
                            }`}
                          >
                            <span className="dot" />
                            {stock.riskTier}
                          </span>
                          <small>{stock.dataQuality}</small>
                        </td>
                        <td>
                          <span>{setupLabel(deepDive)}</span>
                          {ai?.riskFlags.length ? (
                            <small>{ai.riskFlags[0]}</small>
                          ) : null}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="tp-btn tp-btn-secondary tp-btn-sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              runDeepDive(stock.ticker);
                              setSelectedTicker(stock.ticker);
                            }}
                            disabled={deepDive?.status === "running"}
                          >
                            {deepDive?.status === "running" ? "Generating" : "Deep Dive"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="tp-tile tp-moonshot-detail-pane">
          {selectedStock ? (
            <MoonshotDetail
              stock={selectedStock}
              deepDive={deepDives[selectedStock.ticker]}
              aiRank={aiRankByTicker.get(selectedStock.ticker)}
              sample={snapshot.sample}
              runDeepDive={runDeepDive}
            />
          ) : (
            <div className="tp-empty-state">Select a ranked row to inspect it.</div>
          )}
        </aside>
      </div>
    </div>
  );
};

const MoonshotDetail = ({
  stock,
  deepDive,
  aiRank,
  sample,
  runDeepDive,
}: {
  stock: MoonshotStockSignal;
  deepDive: DeepDiveState | undefined;
  aiRank: MoonshotAIRankResult["rows"][number] | undefined;
  sample: boolean;
  runDeepDive: (ticker: string) => void;
}) => (
  <div className="tp-moonshot-detail">
    <header className="tp-moonshot-detail-head">
      <div>
        <h2>
          {stock.ticker} <span>#{stock.rank}</span>
        </h2>
        <p>{stock.companyName}</p>
        <small>{stock.sector}</small>
      </div>
      <div className="tp-moonshot-detail-score">
        <span>Score</span>
        <strong className="num">{stock.researchPriorityScore}</strong>
        {aiRank ? <small>AI #{aiRank.aiRank} · {aiRank.aiScore}</small> : null}
      </div>
    </header>

    <section>
      <h2>Why ranked</h2>
      <div className="tp-moonshot-reasons detail">
        {stock.rankReasons.map((reason) => (
          <span key={`${stock.ticker}-${reason}`}>{reason}</span>
        ))}
      </div>
      {aiRank ? (
        <div className="tp-moonshot-ai-read">
          <strong>AI review</strong>
          <p>{aiRank.aiReason}</p>
          {aiRank.riskFlags.length ? (
            <small>{aiRank.riskFlags.join(" · ")}</small>
          ) : null}
        </div>
      ) : null}
    </section>

    <section>
      <h2>Signals</h2>
      <div className="tp-moonshot-metrics">
        <div>
          <span>Price / move</span>
          <strong className="num">
            {formatPrice(stock.currentPrice)} · {formatPercent(stock.percentChange)}
          </strong>
          <small>{stock.dataDelay}</small>
        </div>
        <div>
          <span>Unusual volume</span>
          <strong className="num">{stock.unusualVolumeRatio.toFixed(1)}x</strong>
          <small>30-day average baseline</small>
        </div>
        <div>
          <span>Next earnings date</span>
          <strong>{formatDate(stock.nextEarningsDate)}</strong>
          <small>Calendar signal only</small>
        </div>
        <div>
          <span>Data quality</span>
          <strong>{stock.dataQuality}</strong>
          <small>Missing providers reduce priority.</small>
        </div>
      </div>
      <div className="tp-moonshot-deep-dive-actions">
        <button
          type="button"
          className="tp-btn tp-btn-secondary tp-btn-sm"
          onClick={() => runDeepDive(stock.ticker)}
          disabled={deepDive?.status === "running"}
        >
          {deepDive?.status === "running" ? "Generating" : "Deep Dive"}
        </button>
        {deepDive ? (
          <span
            className={`tp-inline-status ${
              deepDive.status === "failed" ? "bad" : ""
            }`}
          >
            {deepDive.message}
          </span>
        ) : null}
      </div>
      {deepDive?.result?.suggestedSignal ? (
        <SuggestedSetupPanel signal={deepDive.result.suggestedSignal} />
      ) : null}
    </section>

    <section>
      <h2>Catalyst Watch</h2>
      <p className="tp-moonshot-note">
        Upcoming catalysts are calendar items only, not time-to-enter signals.
      </p>
      {stock.catalysts.length === 0 ? (
        <div className="tp-empty-state">
          Catalyst calendar provider not wired for this row.
        </div>
      ) : (
        <ul className="tp-moonshot-list-rows">
          {stock.catalysts.map((catalyst) => (
            <li key={`${stock.ticker}-${catalyst.date}-${catalyst.label}`}>
              <span className="date">{formatDate(catalyst.date)}</span>
              <span>
                <strong>{catalyst.label}</strong>
                <small>
                  {catalyst.note} · {catalyst.source}
                </small>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>

    <section>
      <h2>Recent News</h2>
      {stock.recentNews.length === 0 ? (
        <div className="tp-empty-state">No recent provider headlines returned.</div>
      ) : (
        <ul className="tp-moonshot-list-rows">
          {stock.recentNews.map((item) => (
            <li key={`${stock.ticker}-${item.date}-${item.headline}`}>
              <span className="date">{formatDate(item.date)}</span>
              <span>
                <strong>{item.headline}</strong>
                <small>{item.source}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>

    <section>
      <h2>Public Disclosures</h2>
      <div className="tp-moonshot-disclosure">
        <strong className="num">{stock.publicDisclosures.count}</strong>
        <span>
          Public-filing data signal only. {stock.publicDisclosures.note} This is
          not a reason to trade.
        </span>
      </div>
    </section>

    <section className="tp-moonshot-risk-read">
      <h2>Risk read</h2>
      <p>{stock.riskRead}</p>
    </section>

    <div className="tp-moonshot-sample-meta">
      {sample && stock.sampleAsOf
        ? `Sample snapshot as of ${formatDate(stock.sampleAsOf)}. `
        : ""}
      Market data as of {formatDate(stock.dataAsOf)} · {stock.dataDelay}.
    </div>
  </div>
);
